(function () {
  'use strict';

  const instances = new WeakMap();

  class StackedCarousel {
    constructor(sectionEl) {
      this.stage = sectionEl.querySelector('.tr-stage');
      this.cardsContainer = sectionEl.querySelector('.tr-cards');

      if (!this.stage || !this.cardsContainer) return;

      this.cards = Array.from(this.cardsContainer.querySelectorAll('.tr-card'));
      if (!this.cards.length) return;

      this.selectedIndex = Math.floor(this.cards.length / 2);
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.rafId = null;
      this.startX = 0;
      this.isSwiping = false;
      this.SWIPE_THRESHOLD = 50;
      this.autoRotateTimer = null;
      this._hovering = false;
      this._focused = false;

      this.autoRotateEnabled = this.stage.dataset.autoRotate === 'true';
      this.autoRotateMs = (parseInt(this.stage.dataset.autoRotateInterval, 10) || 5) * 1000;

      // Bound references for cleanup
      this._onResize = () => this.recalcLayout();
      this._onMouseMove = (e) => this.handleMove(e);
      this._onMouseUp = (e) => this.handleEnd(e);

      this.attachHandlers();
      this.cards.forEach((c, i) => c.setAttribute('data-index', i));
      window.addEventListener('resize', this._onResize);
      window.addEventListener('mousemove', this._onMouseMove);
      window.addEventListener('mouseup', this._onMouseUp);

      this.startupStabilizeAndReveal();
    }

    get total() { return this.cards.length; }
    get center() { return Math.floor(this.total / 2); }

    setCardVars(card, vars) {
      for (const k in vars) card.style.setProperty(k, vars[k]);
    }

    clamp(v, a, b) { return Math.max(a, Math.min(b, v)); }

    recalcLayout() {
      if (this.rafId) cancelAnimationFrame(this.rafId);
      this.rafId = requestAnimationFrame(() => {
        const stageRect = this.stage.getBoundingClientRect();
        const stageW = Math.max(280, stageRect.width);

        if (!this.cards.length) return;

        const cw = Math.max(
          40,
          this.cards[0].offsetWidth || this.cards[0].getBoundingClientRect().width
        );

        const section = this.stage.closest('.tr-carousel-section');
        const cssGapMax =
          parseInt(getComputedStyle(section).getPropertyValue('--gap-max')) || 180;

        const maxOffset = Math.floor(this.total / 2) || 1;

        // Calculate the maximum decaying multiplier for outer cards
        let sumMax = 0;
        for (let j = 1; j <= maxOffset; j++) {
          sumMax += Math.pow(0.78, j - 1);
        }

        // minSpacing = 24px on mobile to force cards to be spread out even with many cards
        const minSpacing = stageW <= 480 ? 24 : 40;
        const maxSpacing = stageW <= 480 ? Math.max(24, cssGapMax) : cssGapMax;

        let spacing = (stageW - cw * 0.8) / (2.1 * sumMax);
        spacing = this.clamp(spacing, minSpacing, maxSpacing);

        const BASE_CENTER_SCALE = 1.06;
        const MIN_CENTER_SCALE = 1.02;
        let centerScale =
          stageW < 480
            ? Math.max(MIN_CENTER_SCALE, (stageW / 480) * BASE_CENTER_SCALE)
            : BASE_CENTER_SCALE;

        const maxCenterScaleByWidth = (stageW * 0.92) / cw;
        if (centerScale > maxCenterScaleByWidth)
          centerScale = this.clamp(centerScale, MIN_CENTER_SCALE, maxCenterScaleByWidth);

        this.cards.forEach((card, i) => {
          const offset = i - this.selectedIndex;
          const isCenter = offset === 0;

          let tx = 0;
          if (!isCenter) {
            const absOffset = Math.abs(offset);
            let sum = 0;
            for (let j = 1; j <= absOffset; j++) {
              sum += Math.pow(0.78, j - 1);
            }
            tx = Math.sign(offset) * spacing * sum;
          }
          const rot = offset * -4;
          const tz = Math.max(-220, -Math.abs(offset) * 30);
          const sideScale = Math.max(0.65, 1 - Math.abs(offset) * 0.06);
          const scale = isCenter ? centerScale : Math.min(sideScale, centerScale - 0.03);
          const delay = Math.min(250, Math.pow(Math.abs(offset), 1.4) * 28);

          const hasNoTrans = card.classList.contains('tr-no-transition');
          card.style.transitionDelay = hasNoTrans ? '0ms' : `${delay}ms`;

          this.setCardVars(card, {
            '--tx': tx + 'px',
            '--tz': tz + 'px',
            '--rot': rot + 'deg',
            '--scale': scale,
          });
          card.classList.toggle('tr-is-selected', isCenter);
          card.setAttribute('aria-selected', isCenter ? 'true' : 'false');
          card.style.zIndex = String(800 - Math.abs(offset) * 10);
        });

      });
    }

    bringIndexToCenter(clickedIdx, focus = true) {
      const center = this.center;
      const n = this.total;
      let diff = clickedIdx - this.selectedIndex;
      if (diff > n / 2) diff -= n;
      if (diff < -n / 2) diff += n;

      if (diff > 0) {
        for (let i = 0; i < diff; i++) {
          this.cardsContainer.appendChild(this.cardsContainer.firstElementChild);
        }
      } else if (diff < 0) {
        for (let i = 0; i < -diff; i++) {
          this.cardsContainer.insertBefore(
            this.cardsContainer.lastElementChild,
            this.cardsContainer.firstElementChild
          );
        }
      }

      this.cards = Array.from(this.cardsContainer.querySelectorAll('.tr-card'));
      this.selectedIndex = center;
      this.recalcLayout();
      if (focus) this.cards[this.selectedIndex]?.focus({ preventScroll: true });
    }

    rotateCards(direction) {
      const center = this.center;
      if (direction === 1) {
        this.cardsContainer.appendChild(this.cardsContainer.firstElementChild);
      } else if (direction === -1) {
        this.cardsContainer.insertBefore(
          this.cardsContainer.lastElementChild,
          this.cardsContainer.firstElementChild
        );
      }
      this.cards = Array.from(this.cardsContainer.querySelectorAll('.tr-card'));
      this.selectedIndex = center;
      this.recalcLayout();
    }

    startAutoRotate() {
      if (!this.autoRotateEnabled || this._hovering || this._focused) return;
      this.stopAutoRotate();
      this.autoRotateTimer = setInterval(() => this.rotateCards(1), this.autoRotateMs);
    }

    stopAutoRotate() {
      clearInterval(this.autoRotateTimer);
      this.autoRotateTimer = null;
    }

    handleStart(event) {
      if (event.type.startsWith('touch')) {
        this.startX = event.touches[0].clientX;
      } else {
        this.startX = event.clientX;
        if (event.target === this.stage || event.target.closest('.tr-stage')) {
          this.isSwiping = true;
          this.cardsContainer.style.cursor = 'grabbing';
        }
      }
    }

    handleMove(event) {
      if (!this.isSwiping && event.type.startsWith('mouse')) return;
    }

    handleEnd(event) {
      if (!this.isSwiping && event.type.startsWith('mouse')) return;
      this.cardsContainer.style.cursor = '';
      this.isSwiping = false;

      let finalX;
      if (event.type.startsWith('touch')) {
        finalX = event.changedTouches[0].clientX;
      } else {
        finalX = event.clientX;
      }

      const diff = finalX - this.startX;
      if (Math.abs(diff) > this.SWIPE_THRESHOLD) {
        this.rotateCards(diff < 0 ? 1 : -1);
      }
    }

    attachHandlers() {
      this.stage.addEventListener('mousedown', (e) => this.handleStart(e));
      this.stage.addEventListener('touchstart', (e) => this.handleStart(e), { passive: true });
      this.stage.addEventListener('touchmove', (e) => this.handleMove(e), { passive: true });
      this.stage.addEventListener('touchend', (e) => this.handleEnd(e));
      this.stage.addEventListener('touchcancel', (e) => this.handleEnd(e));

      // Capture phase: intercept non-center card clicks before any onclick fires
      this.cards.forEach((card) => {
        card.addEventListener(
          'click',
          (ev) => {
            const idx = this.cards.indexOf(card);
            if (idx === -1) return;
            if (idx !== this.selectedIndex) {
              ev.preventDefault();
              ev.stopPropagation();
              this.bringIndexToCenter(idx, true);
            }
          },
          true
        );
      });

      // Bubble phase: navigate center card on clean click
      this.cardsContainer.addEventListener('click', (ev) => {
        if (Math.abs(this.startX - (ev.clientX || 0)) > 5 && !ev.type.startsWith('touch')) return;
        const btn = ev.target.closest('.tr-card');
        if (!btn) return;
        const idx = this.cards.indexOf(btn);
        if (idx === -1 || idx !== this.selectedIndex) return;
        const href = btn.dataset.href;
        if (href) {
          window.location.href = href;
        } else {
          this.recalcLayout();
          btn.focus({ preventScroll: true });
        }
      });

      this.stage.addEventListener('keydown', (ev) => {
        if (ev.key === 'ArrowRight') {
          ev.preventDefault();
          this.rotateCards(1);
        } else if (ev.key === 'ArrowLeft') {
          ev.preventDefault();
          this.rotateCards(-1);
        }
      });

      // Auto-rotate: pause on hover or focus, resume when both are gone
      this.stage.addEventListener('mouseenter', () => {
        this._hovering = true;
        this.stopAutoRotate();
      });
      this.stage.addEventListener('mouseleave', () => {
        this._hovering = false;
        if (!this._focused) this.startAutoRotate();
      });
      this.stage.addEventListener('focusin', () => {
        this._focused = true;
        this.stopAutoRotate();
      });
      this.stage.addEventListener('focusout', (e) => {
        if (!this.stage.contains(e.relatedTarget)) {
          this._focused = false;
          if (!this._hovering) this.startAutoRotate();
        }
      });
    }

    applyStackedStartupState() {
      this.cards.forEach((c) => {
        c.classList.add('tr-no-transition');
        c.style.setProperty('--tx', '0px');
        c.style.setProperty('--tz', '-60px');
        c.style.setProperty('--rot', '0deg');
        c.style.setProperty('--scale', '0.92');
        c.style.opacity = '0';
      });
      this.cardsContainer.setAttribute('aria-hidden', 'true');
    }

    startupStabilizeAndReveal() {
      this.applyStackedStartupState();

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (this.reduceMotion) {
            this.cards.forEach((c) => c.classList.remove('tr-no-transition'));
            this.cardsContainer.removeAttribute('aria-hidden');
            this.selectedIndex = Math.floor(this.cards.length / 2);
            this.recalcLayout();
            this.startAutoRotate();
            return;
          }

          this.cardsContainer.classList.remove('tr-no-transition');
          setTimeout(() => {
            this.cards.forEach((c) => c.classList.remove('tr-no-transition'));
            this.cardsContainer.removeAttribute('aria-hidden');
            this.selectedIndex = Math.floor(this.cards.length / 2);
            this.recalcLayout();

            setTimeout(() => {
              this.cards.forEach((c) => (c.style.opacity = '1'));
              this.startAutoRotate();
            }, 8);
          }, 20);
        });
      });
    }

    destroy() {
      this.stopAutoRotate();
      window.removeEventListener('resize', this._onResize);
      window.removeEventListener('mousemove', this._onMouseMove);
      window.removeEventListener('mouseup', this._onMouseUp);
    }
  }

  function initAll() {
    document.querySelectorAll('.tr-carousel-section:not([data-carousel-ready])').forEach((el) => {
      el.dataset.carouselReady = 'true';
      instances.set(el, new StackedCarousel(el));
    });
  }

  initAll();

  document.addEventListener('shopify:section:load', (e) => {
    const el = e.target.querySelector('.tr-carousel-section');
    if (!el) return;
    const prev = instances.get(el);
    if (prev) prev.destroy();
    delete el.dataset.carouselReady;
    el.dataset.carouselReady = 'true';
    instances.set(el, new StackedCarousel(el));
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const el = e.target.querySelector('.tr-carousel-section');
    if (!el) return;
    instances.get(el)?.destroy();
    instances.delete(el);
    delete el.dataset.carouselReady;
  });
})();
